import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AnalyticsEvent, AnalyticsService } from '../../core/services/analytics-service';
import { ApiService } from '../../core/services/api.service';
import { AuthStoreService } from '../../shared/services/auth/auth-store.service';
import { IOnboardingState, OnboardingStep, OnboardingStoreService } from './onboarding-store.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {

  constructor(
    private apiService: ApiService,
    private authStoreService: AuthStoreService,
    private analytics: AnalyticsService,
    private onboardingStoreService: OnboardingStoreService,
  ) {
    this.init().subscribe();
  }

  get finished(): boolean {
    return this.onboardingStoreService.state.finished;
  }

  get skipped(): boolean {
    return this.onboardingStoreService.state.skipped;
  }

  get active(): boolean {
    return !this.finished && !this.skipped;
  }

  public continue(step: OnboardingStep): void {
    this.setState({ ...this.onboardingStoreService.state, finished: false, skipped: false });
    this.setStep(step);

    this.analytics.trackEvent(new AnalyticsEvent('Activateonboardingstep'));
    this.apiService.put(`/customers/${this.authStoreService.userInfo.customer_id}/onboarding`,
      this.onboardingStoreService.state).subscribe();
  }

  public finish(): void {
    this.setState({ ...this.onboardingStoreService.state, finished: true, step: 'FINISHED' });
    if (this.authStoreService.userInfo) {
      this.analytics.trackEvent(new AnalyticsEvent('Finishonboarding'));
      this.apiService
        .put(`/customers/${this.authStoreService.userInfo.customer_id}/onboarding`, this.onboardingStoreService.state)
        .subscribe();
    }
  }

  public skip(): Observable<any> {
    this.setState({ ...this.onboardingStoreService.state, skipped: true });

    this.analytics.trackEvent(new AnalyticsEvent('Skiponboarding'));
    return this.apiService.put(`/customers/${this.authStoreService.userInfo.customer_id}/onboarding`, this.onboardingStoreService.state);
  }

  public sawFinishScreen(): void {
    this.setState({ ...this.onboardingStoreService.state, finishScreenSeen: true });
    this.finish();
  }

  public setState(nextState: IOnboardingState): void {
    this.onboardingStoreService.setState(nextState);
  }

  public setStep(step: OnboardingStep): void {
    this.setState({ ...this.onboardingStoreService.state, step });
    this.apiService
      .put(`/customers/${this.authStoreService.userInfo.customer_id}/onboarding`, {
        ...this.onboardingStoreService.state,
        step,
      })
      .subscribe();
  }

  public changeStep(step: OnboardingStep): void {
    this.setState({ ...this.onboardingStoreService.state, step });
  }

  public init(): Observable<any> {
    if (this.authStoreService.isAuthenticated && this.authStoreService.userInfo.customer_id) {
      return this.apiService
        .get<IOnboardingState>(`/customers/${this.authStoreService.userInfo.customer_id}/onboarding`)
        .pipe(tap((state) => this.setState(state)));
    }
    return of();
  }

  public shouldNavigate(): boolean {
    return this.onboardingStoreService.state && this.active;
  }

  public step(): OnboardingStep {
    return this.onboardingStoreService.state.step;
  }

  public state() {
    return this.onboardingStoreService.state;
  }

  public clear() {
    this.onboardingStoreService.clear();
  }
}
